// Low-data mode (PARITY P299; ios/Sources/Views/AttachmentViews.swift `deferredForData`, Android
// MediaAutoLoad): on a link the browser calls expensive (cellular) or constrained (Data Saver), a
// picture waits for a tap and text gets the bandwidth. A picture already in the browser's cache
// always shows (asked with `cache: 'only-if-cached'` — no network), and so does one this tab sent
// (its own bytes). Nothing is hidden: the tile keeps its size, says why, and loads on request.
// Re-decided when the link changes — back on Wi-Fi, held pictures fill in by themselves.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { MediaAutoLoad, type MediaLink } from '../../../core/mediaAutoLoad';
import { NetworkLink, type ConnectionInfo } from '../../../core/networkLink';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import './extras.css';

type Connection = ConnectionInfo & EventTarget;

function connection(): Connection | null {
  return (navigator as Navigator & { connection?: Connection }).connection ?? null;
}

let snapshot: MediaLink = NetworkLink.fromConnection(typeof navigator === 'undefined' ? null : connection());

function subscribe(listener: () => void): () => void {
  const c = connection();
  if (!c?.addEventListener) return () => undefined;
  const on = () => {
    snapshot = NetworkLink.fromConnection(c);
    listener();
  };
  c.addEventListener('change', on);
  return () => c.removeEventListener('change', on);
}

/** Re-read on every render (a change before any tile subscribed must not be missed), same object while equal. */
function current(): MediaLink {
  const next = NetworkLink.fromConnection(connection());
  if (next.isExpensive !== snapshot.isExpensive || next.isConstrained !== snapshot.isConstrained) snapshot = next;
  return snapshot;
}

/** What the browser says about the link now (MediaAutoLoad's `MediaLink`). */
export function useMediaLink(): MediaLink {
  return useSyncExternalStore(subscribe, current);
}

/** Pictures the reader asked for this session ("load it anyway" is remembered per picture). */
const requested = new Set<string>();

/** Whether `src` is in the HTTP cache — no request leaves the browser either way. */
async function inCache(src: string): Promise<boolean> {
  try {
    const r = await fetch(src, { cache: 'only-if-cached', mode: 'same-origin', credentials: 'same-origin' });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * The tile's decision: 'load' | 'defer' | 'checking' (deferring, asking the cache first — the
 * placeholder shows meanwhile, never a request).
 */
export function useLowDataDecision(fileId: string, src: string, isLocal: boolean): { decision: 'load' | 'defer'; load(): void } {
  const link = useMediaLink();
  const [asked, setAsked] = useState(() => requested.has(fileId));
  const [cached, setCached] = useState(false);
  const policy = MediaAutoLoad.decide(link, isLocal || cached, asked);
  useEffect(() => {
    if (policy !== 'defer' || cached) return;
    let live = true;
    void inCache(src).then((hit) => live && hit && setCached(true));
    return () => {
      live = false;
    };
  }, [policy, src, cached]);
  return {
    decision: policy,
    load: () => {
      requested.add(fileId);
      setAsked(true);
    },
  };
}

/** The held picture's face: its footprint, an icon, «Toca para cargar», why. */
export function DeferredTile({ w, h, onLoad }: { w: number; h: number; onLoad(): void }) {
  return (
    <span
      className="low-data-tile"
      role="button"
      tabIndex={0}
      style={{ width: w, height: h }}
      title={t('channel.lowDataReason')}
      aria-label={t('web.extras.lowData.load')}
      data-low-data=""
      onClick={(e) => {
        // Inside the tile's link: load here, never open the viewer on a picture that is not there.
        e.preventDefault();
        e.stopPropagation();
        onLoad();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        onLoad();
      }}
    >
      <Icon name="download" size={20} />
      <span className="low-data-tap">{t('channel.lowDataTapToLoad')}</span>
      <span className="low-data-reason">{t('channel.lowDataReason')}</span>
    </span>
  );
}
